import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  Add,
  ChevronLeft,
  ChevronRight,
  DeleteOutline,
  Edit,
  EventAvailable,
  FileCopyOutlined,
  Public,
  Colorize,
} from "@material-ui/icons";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import ConfirmationModal from "../../components/ConfirmationModal";
import ChatbotModal from "../../components/ChatbotModal";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { getBackendUrl } from "../../config";
import { AuthContext } from "../../context/Auth/AuthContext";
import { usePrivacyCensorSettings } from "../../utils/privacyCensor";
import ColorPicker, {
  isValidHexColor,
  normalizeHexColor,
  saveCustomColor,
} from "../../components/ColorPicker";

const stageColors = [
  "#E8F3FF",
  "#E7F8EF",
  "#F0ECFF",
  "#FFF0E6",
  "#FFEFF4",
  "#E9F7F8",
  "#F7F0DF",
  "#EEF2F7",
];

const weekdays = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miercoles",
  "Jueves",
  "Viernes",
  "Sabado",
];

const getBrowserTimeZone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "";

const timeZoneOptions =
  typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone")
    : [
        "America/Costa_Rica",
        "America/Mexico_City",
        "America/Bogota",
        "America/New_York",
        "America/Los_Angeles",
        "Europe/Madrid",
        "UTC",
      ];

const NO_SERVICE_CONNECTION = "no-service-connection";
const NO_SERVICE_USER = "no-service-user";
const NO_REMINDER_SEQUENCE = "no-reminder-sequence";
const ALL_SERVICES_FILTER = "all-services-filter";
const ALL_STATUSES_FILTER = "all-statuses-filter";
const NO_CUSTOM_FIELD_CONDITION = "no-custom-field-condition";

const customFieldTypes = [
  { value: "text", label: "Texto" },
  { value: "number", label: "Numero" },
  { value: "date", label: "Fecha" },
  { value: "email", label: "Correo" },
  { value: "phone", label: "Telefono" },
  { value: "textarea", label: "Texto largo" },
  { value: "select", label: "Desplegable" },
  { value: "multiple", label: "Multiple" },
];

const conditionOperators = [
  { value: "equals", label: "Es igual" },
  { value: "not_equals", label: "No es igual" },
  { value: "contains", label: "Contiene" },
];

const emptyConditionalConfig = () => ({
  enabled: false,
  sourceFieldName: "",
  operator: "equals",
  value: "",
});

const toOptionalNull = (value, emptyValue) => (value === emptyValue ? null : value || null);
const toOptionalString = (value, emptyValue) => (value === emptyValue ? "" : value || "");

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    margin: 20,
    padding: theme.spacing(1.5),
    overflow: "auto",
    ...theme.scrollbarStyles,
  },
  toolbar: {
    display: "grid",
    gridTemplateColumns: "minmax(160px, 180px) minmax(220px, 280px) minmax(160px, 220px) minmax(180px, 220px) 1fr",
    gap: theme.spacing(1),
    alignItems: "center",
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  agendaHeader: {
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      alignItems: "flex-start",
    },
  },
  agendaHeaderButtons: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      flexBasis: "100%",
      marginLeft: 0,
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "flex-start",
    },
  },
  calendar: {
    display: "grid",
    gridTemplateColumns: "repeat(7, minmax(150px, 1fr))",
    gap: theme.spacing(1),
    minWidth: 980,
  },
  day: {
    minHeight: 160,
    border: "1px solid rgba(0,0,0,.1)",
    borderRadius: 6,
    background: "#fff",
    padding: theme.spacing(1),
  },
  dayHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.75),
  },
  reservation: {
    borderRadius: 6,
    border: "1px solid",
    padding: theme.spacing(0.75),
    marginBottom: theme.spacing(0.75),
    cursor: "pointer",
  },
  serviceList: {
    display: "grid",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  },
  serviceCard: {
    padding: theme.spacing(1),
    border: "1px solid rgba(0,0,0,.1)",
  },
  resourcesPanel: {
    display: "grid",
    gap: theme.spacing(1.25),
  },
  resourcesHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  resourcesList: {
    display: "grid",
    gap: theme.spacing(1),
  },
  resourceCard: {
    padding: theme.spacing(1.5),
    border: "1px solid rgba(0,0,0,.1)",
  },
  resourceRow: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 1.6fr) minmax(180px, .8fr) minmax(120px, auto) auto",
    gap: theme.spacing(1.25),
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  resourceStatus: {
    minHeight: 40,
    display: "flex",
    alignItems: "center",
  },
  resourceActions: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
    },
  },
  serviceHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  colorDot: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: "1px solid rgba(0,0,0,.12)",
    display: "inline-block",
  },
  colorAdornment: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, 0.18)",
  },
  fieldRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  customFieldBox: {
    border: "1px solid rgba(0,0,0,.1)",
    borderRadius: 6,
    padding: theme.spacing(1.25),
    marginTop: theme.spacing(1),
  },
  customFieldRow: {
    display: "grid",
    gridTemplateColumns: "minmax(180px, 1.4fr) minmax(140px, .8fr) auto auto",
    gap: theme.spacing(1),
    alignItems: "flex-start",
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  customFieldOptions: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  customFieldOptionRow: {
    display: "grid",
    gridTemplateColumns: "minmax(180px, 1fr) auto",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  conditionalRow: {
    background: theme.palette.action.hover,
    borderRadius: 6,
    padding: theme.spacing(1.25),
    marginTop: theme.spacing(1),
  },
  conditionalHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    fontWeight: 600,
  },
  conditionalFields: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  availabilityRow: {
    display: "grid",
    gridTemplateColumns: "120px 1fr 1fr",
    gap: theme.spacing(1),
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  countdownRow: {
    alignItems: "flex-start",
  },
  countdownSummary: {
    display: "grid",
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  countdownCard: {
    padding: theme.spacing(1),
    border: "1px solid rgba(0,0,0,.1)",
  },
  countdownCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  countdownCardActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  widgetBox: {
    padding: theme.spacing(1),
    border: "1px solid rgba(37,118,210,.25)",
    borderRadius: 6,
    background: "#f5f9ff",
    wordBreak: "break-all",
    fontFamily: '"Inter", sans-serif',
    fontSize: 12,
  },
  serviceActions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    alignItems: "center",
    marginTop: theme.spacing(0.5),
  },
  slotsPanel: {
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 6,
    padding: theme.spacing(1.25),
    marginTop: theme.spacing(0.5),
  },
  slotsHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  slotsNav: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.25),
    minHeight: 32,
  },
  slotResourceBadges: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  slotDay: {
    marginTop: theme.spacing(1),
    "&:first-of-type": {
      marginTop: 0,
    },
  },
  slotGrid: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    justifyContent: "space-between",
    gap: 8,
    marginTop: theme.spacing(0.75),
    marginBottom: 8,
  },
  slotButton: {
    flex: "1 1 96px",
    minWidth: 0,
    borderRadius: 6,
    textTransform: "none",
  },
  selectedSlotButton: {
    flex: "1 1 96px",
    minWidth: 0,
    borderRadius: 6,
    textTransform: "none",
  },
  slotEmpty: {
    padding: theme.spacing(1.25),
    borderRadius: 6,
    background: theme.palette.action.hover,
  },
  censoredText: {
    filter: "blur(4px)",
    userSelect: "none",
  },
  censoredPhoto: {
    filter: "blur(6px)",
  },
}));

const pad = value => String(value).padStart(2, "0");

const getMonthDays = currentDate => {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const first = new Date(year, month, 1);
  const start = startOfWeek(first);
  return Array.from({ length: 42 }).map((_, index) => {
    const day = new Date(start);
    day.setDate(start.getDate() + index);
    return day;
  });
};

const addDays = (date, amount) => {
  const next = new Date(date);
  next.setDate(next.getDate() + amount);
  return next;
};

const startOfWeek = date => {
  const start = new Date(date);
  const mondayOffset = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - mondayOffset);
  start.setHours(0, 0, 0, 0);
  return start;
};

const startOfMonth = date => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  start.setHours(0, 0, 0, 0);
  return start;
};

const toDateInput = date =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const getDatePartsInTimeZone = (value, timeZone) => {
  if (!value) return "";
  const date = new Date(value);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const values = parts.reduce((acc, part) => {
    if (part.type !== "literal") acc[part.type] = Number(part.value);
    return acc;
  }, {});

  return {
    year: values.year,
    month: values.month,
    day: values.day,
    hour: values.hour === 24 ? 0 : values.hour,
    minute: values.minute,
    second: values.second,
  };
};

const toDateInputInTimeZone = (value, timeZone) => {
  const parts = getDatePartsInTimeZone(value, timeZone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`;
};

const toDateTimeInput = (value, timeZone) => {
  if (!value) return "";
  const parts = getDatePartsInTimeZone(value, timeZone);
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
};

const formatTime12 = (value, timeZone) =>
  new Date(value).toLocaleTimeString("en-US", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

const reservationStatusLabels = {
  pending: "Pendiente",
  confirmed: "Confirmada",
  cancelled: "Cancelada",
  rescheduled: "Reprogramada",
  completed: "Completada",
  no_show: "No asistio",
};

const formatReservationStatus = status =>
  reservationStatusLabels[status] || status || "";

const reservationStatusOptions = Object.entries(reservationStatusLabels);

const agendaReminderStatusLabels = {
  pending: "Pendiente",
  processing: "Procesando",
  sent: "Enviado",
  failed: "Fallido",
};

const formatAgendaReminderStatus = status =>
  agendaReminderStatusLabels[status] || status || "";

const formatDateTimeInTimeZone = (value, timeZone) => {
  if (!value) return "";
  return new Date(value).toLocaleString("es-CR", {
    timeZone,
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
};

const formatSlotDayLabel = value => {
  const [year, month, day] = String(value || "").split("-").map(Number);
  if (!year || !month || !day) return "";
  return new Date(year, month - 1, day).toLocaleDateString("es-CR", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
};

const emptyService = {
  id: null,
  name: "",
  description: "",
  color: stageColors[0],
  timeZone: getBrowserTimeZone(),
  durationMinutes: 30,
  intervalMinutes: 30,
  bufferBeforeMinutes: 0,
  bufferAfterMinutes: 0,
  maxBookingsPerSlot: 1,
  hasPersons: false,
  minPersons: 1,
  maxPersons: "",
  countPersonsAsCapacity: false,
  hasResources: false,
  connectionId: NO_SERVICE_CONNECTION,
  userId: NO_SERVICE_USER,
  isActive: true,
  availability: [0, 1, 2, 3, 4, 5, 6].map(day => ({
    day,
    enabled: day > 0 && day < 6,
    startTime: "09:00",
    endTime: "17:00",
  })),
  customFields: [],
  reminders: [],
  serviceResources: [],
};

const emptyReservation = {
  id: null,
  serviceId: "",
  contactId: "",
  startsAt: "",
  status: "confirmed",
  customerName: "",
  customerPhone: "",
  customerEmail: "",
  participants: 1,
  notes: "",
  customFields: [],
};

const normalizeCustomFieldConditionalPayload = config => {
  const nextConfig = {
    ...emptyConditionalConfig(),
    ...(config || {}),
  };

  return {
    enabled: nextConfig.enabled === true,
    sourceFieldName: String(nextConfig.sourceFieldName || "").trim(),
    operator: conditionOperators.some(operator => operator.value === nextConfig.operator)
      ? nextConfig.operator
      : "equals",
    value: String(nextConfig.value || "").trim(),
  };
};

const normalizeCustomFieldOptions = (options, keepEmpty = false) =>
  (Array.isArray(options) ? options : [])
    .map(option => (typeof option === "string" ? option : option?.label || option?.name || ""))
    .map(option => String(option || "").trim())
    .filter(option => keepEmpty || option);

const normalizeCustomFieldType = type =>
  customFieldTypes.some(fieldType => fieldType.value === type) ? type : "text";

const normalizeServiceCustomFields = fields =>
  (Array.isArray(fields) ? fields : []).map(field => ({
    name: field.name || "",
    required: field.required === true,
    type: normalizeCustomFieldType(field.type || "text"),
    options: normalizeCustomFieldOptions(field.options, true),
    conditionalConfig: normalizeCustomFieldConditionalPayload(field.conditionalConfig),
  }));

const normalizeComparableText = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const customFieldMatchesCondition = (field, fields = []) => {
  const config = normalizeCustomFieldConditionalPayload(field?.conditionalConfig);
  if (!config.enabled) return true;
  if (!config.sourceFieldName || !config.value) return true;

  const sourceField = fields.find(item => normalizeComparableText(item.name) === normalizeComparableText(config.sourceFieldName));
  if (!sourceField || !String(sourceField.value || "").trim()) return false;

  const expectedValue = normalizeComparableText(config.value);
  const valuesToCompare = String(sourceField.value || "")
    .split(",")
    .map(value => normalizeComparableText(value))
    .filter(Boolean);

  if (config.operator === "not_equals") {
    return !valuesToCompare.some(value => value === expectedValue);
  }

  if (config.operator === "contains") {
    return valuesToCompare.some(value => value.includes(expectedValue));
  }

  return valuesToCompare.some(value => value === expectedValue);
};

const getVisibleCustomFields = fields =>
  (fields || []).reduce((visibleFields, field) => {
    const config = normalizeCustomFieldConditionalPayload(field?.conditionalConfig);
    if (
      config.enabled &&
      config.sourceFieldName &&
      !visibleFields.some(sourceField => normalizeComparableText(sourceField.name) === normalizeComparableText(config.sourceFieldName))
    ) {
      return visibleFields;
    }

    return customFieldMatchesCondition(field, fields)
      ? [...visibleFields, field]
      : visibleFields;
  }, []);

const buildReservationCustomFields = (service, reservationFields = []) => {
  const fields = Array.isArray(reservationFields) ? reservationFields : [];
  const serviceFields = normalizeServiceCustomFields(service?.customFields);

  if (!serviceFields.length) return fields;

  const normalizedFields = serviceFields
    .map(field => {
      const name = String(field.name || "").trim();
      if (!name) return null;
      const savedField = fields.find(item => String(item.name || "") === name);
      return {
        ...field,
        name,
        value: savedField?.value || "",
      };
    })
    .filter(Boolean);

  const extraFields = fields.filter(
    field => !normalizedFields.some(item => item.name === field.name)
  );

  return [...normalizedFields, ...extraFields];
};

const normalizeServiceForm = service => ({
  ...emptyService,
  ...service,
  connectionId: service?.connectionId || NO_SERVICE_CONNECTION,
  userId: service?.userId || NO_SERVICE_USER,
  maxPersons: service?.maxPersons || "",
  serviceResources: (service?.serviceResources || []).map(rule => ({
    resourceId: rule.resourceId || rule.resource?.id || "",
    requirementType: rule.requirementType || "fixed",
    quantity: rule.quantity || 1,
    assignment: rule.assignment || "required",
  })),
  customFields: normalizeServiceCustomFields(service?.customFields),
  reminders: (service?.reminders || []).map(reminder => ({
    ...reminder,
    sequenceId: reminder.sequenceId ? String(reminder.sequenceId) : NO_REMINDER_SEQUENCE,
  })),
});

const sanitizeServiceReminders = reminders =>
  (reminders || []).map(reminder => ({
    ...reminder,
    sequenceId: toOptionalString(reminder.sequenceId, NO_REMINDER_SEQUENCE),
  }));

const sanitizeServiceResources = serviceResources =>
  (serviceResources || [])
    .map(rule => ({
      resourceId: rule.resourceId ? Number(rule.resourceId) : null,
      requirementType: rule.requirementType === "perPerson" ? "perPerson" : "fixed",
      quantity: Number(rule.quantity || 1),
      assignment: rule.assignment || "required",
    }))
    .filter(rule => rule.resourceId && rule.quantity > 0);

const Agenda = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const privacyCensorSettings = usePrivacyCensorSettings(user?.id);
  const [services, setServices] = useState([]);
  const [resources, setResources] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [connections, setConnections] = useState([]);
  const [users, setUsers] = useState([]);
  const [sequences, setSequences] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [selectedReservationContact, setSelectedReservationContact] = useState(null);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date());
  const [calendarView, setCalendarView] = useState("month");
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [serviceModalContext, setServiceModalContext] = useState(null);
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [serviceForm, setServiceForm] = useState(emptyService);
  const [serviceColorPickerOpen, setServiceColorPickerOpen] = useState(false);
  const [reservationForm, setReservationForm] = useState(emptyReservation);
  const [reservationSlotStartDate, setReservationSlotStartDate] = useState(toDateInput(new Date()));
  const [reservationSlots, setReservationSlots] = useState([]);
  const [loadingReservationSlots, setLoadingReservationSlots] = useState(false);
  const [deletingService, setDeletingService] = useState(null);
  const [deletingResource, setDeletingResource] = useState(null);
  const [deletingReservation, setDeletingReservation] = useState(null);
  const [deletingCountdown, setDeletingCountdown] = useState(null);
  const [tab, setTab] = useState("calendar");
  const [sequenceModalOpen, setSequenceModalOpen] = useState(false);
  const [sequenceModalId, setSequenceModalId] = useState(null);
  const [sequenceReminderIndex, setSequenceReminderIndex] = useState(null);

  const selectedService = services.find(
    service => String(service.id) === String(selectedServiceId)
  );

  const findReservationService = reservation => {
    const serviceId = reservation?.serviceId || reservation?.service?.id;
    const catalogService = services.find(
      service => String(service.id) === String(serviceId)
    );
    const reservationService = reservation?.service;

    if (catalogService?.customFields?.length) return catalogService;
    if (reservationService?.customFields?.length) return reservationService;
    return catalogService || reservationService || selectedService || services[0];
  };
  const reservationService = findReservationService(reservationForm);

  const contactOptions = useMemo(() => {
    if (!selectedReservationContact) return contacts;
    const exists = contacts.some(
      contact => String(contact.id) === String(selectedReservationContact.id)
    );
    return exists ? contacts : [selectedReservationContact, ...contacts];
  }, [contacts, selectedReservationContact]);

  const renderContactOption = option => (
    <>
      <Avatar
        src={privacyCensorSettings.contactPhotos ? undefined : option.profilePicUrl}
        className={privacyCensorSettings.contactPhotos ? classes.censoredPhoto : undefined}
        style={{ marginRight: 8 }}
      />
      <div>
        <Typography
          variant="body2"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames ? "Contacto privado" : option.name || "-"}
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
          className={privacyCensorSettings.contactNames ? classes.censoredText : undefined}
        >
          {privacyCensorSettings.contactNames ? "Telefono privado" : option.number || ""}
        </Typography>
      </div>
    </>
  );

  const calendarDays = useMemo(() => {
    if (calendarView === "day") return [new Date(month)];
    if (calendarView === "week") {
      const start = startOfWeek(month);
      return Array.from({ length: 7 }).map((_, index) => addDays(start, index));
    }
    return getMonthDays(month);
  }, [calendarView, month]);

  const fetchBase = async () => {
    try {
      const [
        { data: serviceData },
        { data: resourceData },
        { data: connectionData },
        { data: userData },
      ] =
        await Promise.all([
          api.get("/appointments/services"),
          api.get("/appointments/resources"),
          api.get("/connections/"),
          api.get("/users"),
        ]);

      setServices(serviceData.services || []);
      setResources(resourceData.resources || []);
      setConnections(connectionData || []);
      setUsers(userData.users || []);
      setSelectedServiceId(prev => prev || serviceData.services?.[0]?.id || "");
    } catch (err) {
      toastError(err);
    }
  };

  const loadSequences = async () => {
    const loadedSequences = [];
    let pageNumber = 1;
    let hasMore = true;

    while (hasMore) {
      const { data } = await api.get("/chatbot/sequences", {
        params: { searchParam: "", pageNumber },
      });
      loadedSequences.push(...(data.sequences || []));
      hasMore = Boolean(data.hasMore);
      pageNumber += 1;
    }

    setSequences(loadedSequences);
    return loadedSequences;
  };

  const fetchReservations = async () => {
    setLoading(true);
    try {
      let start = startOfMonth(month);
      let end = new Date(month.getFullYear(), month.getMonth() + 1, 1);

      if (calendarView === "day") {
        start = new Date(month);
        start.setHours(0, 0, 0, 0);
        end = addDays(start, 1);
      } else if (calendarView === "week") {
        start = startOfWeek(month);
        end = addDays(start, 7);
      } else {
        const visibleDays = getMonthDays(month);
        start = new Date(visibleDays[0]);
        start.setHours(0, 0, 0, 0);
        end = addDays(visibleDays[visibleDays.length - 1], 1);
      }

      const { data } = await api.get("/appointments/reservations", {
        params: {
          start: start.toISOString(),
          end: end.toISOString(),
          serviceId: selectedServiceId || undefined,
          status: selectedStatus || undefined,
        },
      });
      setReservations(data.reservations || []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBase();
    loadSequences().catch(toastError);
  }, []);

  useEffect(() => {
    fetchReservations();
  }, [selectedServiceId, selectedStatus, calendarView, month]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("agenda", data => {
      if (["create", "update", "delete"].includes(data.action)) {
        fetchReservations();
      }
    });

    return () => {
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServiceId, selectedStatus, calendarView, month]);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/contacts", {
          params: { searchParam: contactSearch, pageNumber: 1 },
        });
        setContacts(data.contacts || []);
      } catch (err) {
        toastError(err);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [contactSearch]);

  useEffect(() => {
    if (!reservationModalOpen || !reservationForm.serviceId) {
      setReservationSlots([]);
      setLoadingReservationSlots(false);
      return;
    }

    let mounted = true;

    const fetchReservationSlots = async () => {
      setLoadingReservationSlots(true);
      try {
        const startDate = reservationSlotStartDate || toDateInput(new Date());
        const dates = Array.from({ length: 7 }).map((_, index) =>
          toDateInput(addDays(new Date(`${startDate}T00:00:00`), index))
        );
        const responses = await Promise.all(
          dates.map(date =>
            api.get(`/appointments/services/${reservationForm.serviceId}/availability`, {
              params: { date, participants: reservationForm.participants || 1 },
            })
          )
        );

        if (!mounted) return;

        setReservationSlots(
          responses.map((response, index) => ({
            date: dates[index],
            slots: (response.data.slots || []).filter(slot => slot.available),
          }))
        );
      } catch (err) {
        if (mounted) {
          setReservationSlots([]);
          toastError(err);
        }
      } finally {
        if (mounted) setLoadingReservationSlots(false);
      }
    };

    fetchReservationSlots();

    return () => {
      mounted = false;
    };
  }, [
    reservationModalOpen,
    reservationForm.serviceId,
    reservationForm.participants,
    reservationSlotStartDate,
  ]);

  const closeServiceModal = () => {
    setServiceModalOpen(false);
    setServiceModalContext(null);
  };

  const openServiceModal = (service, context) => {
    setServiceForm(service ? normalizeServiceForm(service) : {
      ...emptyService,
      timeZone: getBrowserTimeZone(),
      color: stageColors[services.length % stageColors.length],
    });
    setServiceModalContext(context || null);
    setServiceModalOpen(true);
  };

  const openEditReservationService = serviceId => {
    if (!serviceId) return;
    const service = services.find(item => String(item.id) === String(serviceId));
    if (!service) return;
    openServiceModal(service, "reservation");
  };

  const openReservationModal = (reservation, day) => {
    const service = findReservationService(reservation);
    const slotStartDate = reservation
      ? toDateInputInTimeZone(reservation.startsAt, service?.timeZone)
      : toDateInput(day || new Date());
    const startsAt = reservation
      ? toDateTimeInput(reservation.startsAt, service?.timeZone)
      : "";
    const reservationContact = reservation?.contact || null;
    setSelectedReservationContact(reservationContact);
    setReservationSlotStartDate(slotStartDate);
    setReservationForm({
      ...emptyReservation,
      ...reservation,
      contactId: reservation?.contactId || reservationContact?.id || "",
      serviceId: reservation?.serviceId || service?.id || "",
      startsAt,
      customerName:
        reservation?.customerName || reservation?.contact?.name || "",
      customerPhone:
        reservation?.customerPhone || reservation?.contact?.number || "",
      customerEmail:
        reservation?.customerEmail || reservation?.contact?.email || "",
      participants: reservation?.participants || 1,
      customFields: buildReservationCustomFields(service, reservation?.customFields || []),
    });
    setReservationModalOpen(true);
  };

  const applyReservationResponse = reservation => {
    const service = findReservationService(reservation);

    setReservationForm(prev => ({
      ...prev,
      ...reservation,
      startsAt: toDateTimeInput(reservation.startsAt, service?.timeZone),
      customFields: buildReservationCustomFields(service, reservation.customFields || []),
    }));
  };

  const handleSaveService = async () => {
    try {
      const customFields = normalizeServiceCustomFields(serviceForm.customFields)
        .map(field => ({
          ...field,
          options: normalizeCustomFieldOptions(field.options),
          conditionalConfig: normalizeCustomFieldConditionalPayload(field.conditionalConfig),
        }))
        .filter(field => field.name);
      const payload = {
        ...serviceForm,
        durationMinutes: Number(serviceForm.durationMinutes || 30),
        intervalMinutes: Number(serviceForm.intervalMinutes || 30),
        bufferBeforeMinutes: Number(serviceForm.bufferBeforeMinutes || 0),
        bufferAfterMinutes: Number(serviceForm.bufferAfterMinutes || 0),
        maxBookingsPerSlot: Number(serviceForm.maxBookingsPerSlot || 1),
        hasPersons: serviceForm.hasPersons === true,
        minPersons: Number(serviceForm.minPersons || 1),
        maxPersons: serviceForm.maxPersons ? Number(serviceForm.maxPersons) : null,
        countPersonsAsCapacity: serviceForm.countPersonsAsCapacity === true,
        hasResources: serviceForm.hasResources === true,
        timeZone: serviceForm.timeZone,
        connectionId: toOptionalNull(serviceForm.connectionId, NO_SERVICE_CONNECTION),
        userId: toOptionalNull(serviceForm.userId, NO_SERVICE_USER),
        customFields,
        reminders: sanitizeServiceReminders(serviceForm.reminders),
        serviceResources: sanitizeServiceResources(serviceForm.serviceResources),
      };

      let savedService;
      if (serviceForm.id) {
        const { data } = await api.put(`/appointments/services/${serviceForm.id}`, payload);
        savedService = data;
      } else {
        const { data } = await api.post("/appointments/services", payload);
        savedService = data;
      }

      toast.success("Servicio guardado");
      if (serviceModalContext === "reservation" && savedService?.id) {
        setReservationForm(prev => ({
          ...prev,
          serviceId: savedService.id,
          startsAt: "",
          customFields: buildReservationCustomFields(
            savedService,
            prev.customFields || []
          ),
        }));
      }
      closeServiceModal();
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveReservation = async () => {
    try {
      const customFields = getVisibleCustomFields(reservationForm.customFields || []);
      const payload = {
        ...reservationForm,
        contactId: selectedReservationContact?.id || reservationForm.contactId || null,
        startsAt: reservationForm.startsAt,
        customFields,
      };

      if (reservationForm.id) {
        await api.put(`/appointments/reservations/${reservationForm.id}`, payload);
      } else {
        await api.post("/appointments/reservations", payload);
      }

      toast.success("Reserva guardada");
      setReservationModalOpen(false);
      fetchReservations();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteService = async () => {
    try {
      await api.delete(`/appointments/services/${deletingService.id}`);
      toast.success("Servicio eliminado");
      setDeletingService(null);
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const handleCreateResource = async () => {
    try {
      await api.post("/appointments/resources", {
        name: "Nuevo recurso",
        quantity: 1,
        isActive: true,
      });
      toast.success("Recurso creado");
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const handleUpdateResource = async (resource, patch) => {
    try {
      await api.put(`/appointments/resources/${resource.id}`, {
        ...resource,
        ...patch,
      });
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteResource = async () => {
    try {
      await api.delete(`/appointments/resources/${deletingResource.id}`);
      toast.success("Recurso eliminado");
      setDeletingResource(null);
      fetchBase();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteReservation = async () => {
    try {
      await api.delete(`/appointments/reservations/${deletingReservation.id}`);
      toast.success("Reserva eliminada");
      setDeletingReservation(null);
      setReservationModalOpen(false);
      setReservationForm(emptyReservation);
      fetchReservations();
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteCountdown = async () => {
    try {
      const { data } = await api.delete(
        `/appointments/countdowns/${deletingCountdown.id}`
      );

      applyReservationResponse(data);
      toast.success("Countdown eliminado");
      setDeletingCountdown(null);
      fetchReservations();
    } catch (err) {
      toastError(err);
    }
  };

  const addServiceField = () => {
    setServiceForm(prev => ({
      ...prev,
      customFields: [
        ...(prev.customFields || []),
        {
          name: "",
          required: false,
          type: "text",
          options: [],
          conditionalConfig: emptyConditionalConfig(),
        },
      ],
    }));
  };

  const addServiceReminder = () => {
    setServiceForm(prev => ({
      ...prev,
      reminders: [
        ...(prev.reminders || []),
        {
          value: 24,
          unit: "hours",
          sequenceId: NO_REMINDER_SEQUENCE,
        },
      ],
    }));
  };

  const addServiceResource = () => {
    setServiceForm(prev => ({
      ...prev,
      hasResources: true,
      serviceResources: [
        ...(prev.serviceResources || []),
        {
          resourceId: resources[0]?.id || "",
          requirementType: prev.hasPersons ? "perPerson" : "fixed",
          quantity: 1,
          assignment: "required",
        },
      ],
    }));
  };

  const updateServiceResource = (index, patch) => {
    const serviceResources = [...(serviceForm.serviceResources || [])];
    serviceResources[index] = { ...serviceResources[index], ...patch };
    setServiceForm({ ...serviceForm, serviceResources });
  };

  const openCreateSequence = index => {
    setSequenceReminderIndex(index);
    setSequenceModalId(null);
    setSequenceModalOpen(true);
  };

  const openEditSequence = (index, sequenceId) => {
    if (!sequenceId || sequenceId === NO_REMINDER_SEQUENCE) return;
    setSequenceReminderIndex(index);
    setSequenceModalId(sequenceId);
    setSequenceModalOpen(true);
  };

  const formatSequenceOption = sequence =>
    `ID: ${sequence.id} - ${sequence.name}`;

  const handleSequenceSaved = async savedSequence => {
    const savedSequenceId = savedSequence?.id || sequenceModalId;
    const nextSequences = await loadSequences();

    if (
      savedSequence &&
      savedSequenceId &&
      !nextSequences.some(sequence => String(sequence.id) === String(savedSequenceId))
    ) {
      setSequences(prevSequences => [...prevSequences, savedSequence]);
    }

    if (sequenceReminderIndex !== null && savedSequenceId) {
      setServiceForm(prev => {
        const reminders = [...(prev.reminders || [])];
        reminders[sequenceReminderIndex] = {
          ...reminders[sequenceReminderIndex],
          sequenceId: String(savedSequenceId),
        };
        return { ...prev, reminders };
      });
    }

    setSequenceModalOpen(false);
    setSequenceModalId(null);
    setSequenceReminderIndex(null);
  };

  const handleSequenceModalClose = () => {
    setSequenceModalOpen(false);
    setSequenceModalId(null);
    setSequenceReminderIndex(null);
  };

  const renderSequenceSelector = (reminder, index) => {
    const selectedSequenceId = reminder.sequenceId || NO_REMINDER_SEQUENCE;

    return (
      <>
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>Secuencia</InputLabel>
          <Select
            value={selectedSequenceId}
            label="Secuencia"
            onChange={event => {
              if (event.target.value === "__create__") {
                openCreateSequence(index);
                return;
              }

              const reminders = [...serviceForm.reminders];
              reminders[index] = {
                ...reminder,
                sequenceId: event.target.value,
              };
              setServiceForm({ ...serviceForm, reminders });
            }}
          >
            <MenuItem value={NO_REMINDER_SEQUENCE}>No ejecutar secuencia</MenuItem>
            <MenuItem value="__create__">Crear secuencia...</MenuItem>
            {sequences.map(sequence => (
              <MenuItem key={sequence.id} value={String(sequence.id)}>
                {formatSequenceOption(sequence)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          color="primary"
          disabled={selectedSequenceId === NO_REMINDER_SEQUENCE}
          onClick={() => openEditSequence(index, selectedSequenceId)}
        >
          Editar secuencia
        </Button>
      </>
    );
  };

  const updateServiceCustomField = (index, patch) => {
    const customFields = normalizeServiceCustomFields(serviceForm.customFields);
    customFields[index] = { ...customFields[index], ...patch };
    setServiceForm({ ...serviceForm, customFields });
  };

  const updateServiceCustomFieldOption = (fieldIndex, optionIndex, value) => {
    const customFields = normalizeServiceCustomFields(serviceForm.customFields);
    const options = [...(customFields[fieldIndex].options || [])];
    options[optionIndex] = value;
    customFields[fieldIndex] = { ...customFields[fieldIndex], options };
    setServiceForm({ ...serviceForm, customFields });
  };

  const renderCustomFieldConditionalControls = (field, fieldIndex) => {
    const conditionalConfig = normalizeCustomFieldConditionalPayload(field.conditionalConfig);
    const sourceFields = normalizeServiceCustomFields(serviceForm.customFields)
      .filter((sourceField, sourceIndex) => sourceIndex < fieldIndex && sourceField.name)
      .map(sourceField => ({
        name: sourceField.name,
        options: sourceField.options || [],
      }));
    const selectedSource = sourceFields.find(
      sourceField => sourceField.name === conditionalConfig.sourceFieldName
    );
    const sourceOptions = (selectedSource?.options || []).filter(Boolean);

    return (
      <div className={classes.conditionalRow}>
        <div className={classes.conditionalHeader}>
          <Checkbox
            checked={conditionalConfig.enabled === true}
            onChange={event =>
              updateServiceCustomField(fieldIndex, {
                conditionalConfig: {
                  ...conditionalConfig,
                  enabled: event.target.checked,
                },
              })
            }
            color="primary"
          />
          <span>Campo condicional</span>
        </div>
        {conditionalConfig.enabled === true && (
          <div className={classes.conditionalFields}>
            <FormControl variant="outlined">
              <InputLabel>Mostrar cuando el campo</InputLabel>
              <Select
                label="Mostrar cuando el campo"
                value={conditionalConfig.sourceFieldName || NO_CUSTOM_FIELD_CONDITION}
                onChange={event =>
                  updateServiceCustomField(fieldIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      sourceFieldName:
                        event.target.value === NO_CUSTOM_FIELD_CONDITION
                          ? ""
                          : event.target.value,
                      value: "",
                    },
                  })
                }
              >
                <MenuItem value={NO_CUSTOM_FIELD_CONDITION}>Seleccionar campo</MenuItem>
                {sourceFields.map(sourceField => (
                  <MenuItem key={sourceField.name} value={sourceField.name}>
                    {sourceField.name}
                  </MenuItem>
                ))}
                {!sourceFields.length && (
                  <MenuItem value="" disabled>
                    Sin campos anteriores
                  </MenuItem>
                )}
              </Select>
            </FormControl>
            <FormControl variant="outlined">
              <InputLabel>Operador</InputLabel>
              <Select
                label="Operador"
                value={conditionalConfig.operator || "equals"}
                onChange={event =>
                  updateServiceCustomField(fieldIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      operator: event.target.value,
                    },
                  })
                }
              >
                {conditionOperators.map(operator => (
                  <MenuItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {sourceOptions.length ? (
              <FormControl variant="outlined">
                <InputLabel>Valor</InputLabel>
                <Select
                  label="Valor"
                  value={conditionalConfig.value || NO_CUSTOM_FIELD_CONDITION}
                  onChange={event =>
                    updateServiceCustomField(fieldIndex, {
                      conditionalConfig: {
                        ...conditionalConfig,
                        value:
                          event.target.value === NO_CUSTOM_FIELD_CONDITION
                            ? ""
                            : event.target.value,
                      },
                    })
                  }
                >
                  <MenuItem value={NO_CUSTOM_FIELD_CONDITION}>Seleccionar valor</MenuItem>
                  {sourceOptions.map((option, optionIndex) => (
                    <MenuItem key={`${optionIndex}-${option}`} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                variant="outlined"
                label="Valor"
                value={conditionalConfig.value || ""}
                onChange={event =>
                  updateServiceCustomField(fieldIndex, {
                    conditionalConfig: {
                      ...conditionalConfig,
                      value: event.target.value,
                    },
                  })
                }
              />
            )}
          </div>
        )}
      </div>
    );
  };

  const widgetScript = service =>
    service
      ? `<script src="${(getBackendUrl() || window.location.origin).replace(/\/$/, "")}/public/appointments/widget/${service.widgetToken}"></script>`
      : "";

  const reservationsByDate = date =>
    reservations.filter(reservation => {
      const timeZone = reservation.service?.timeZone;
      if (!timeZone) return false;
      return toDateInputInTimeZone(reservation.startsAt, timeZone) === toDateInput(date);
    });

  const handleCalendarDateChange = value => {
    if (!value) return;

    if (calendarView === "month") {
      const [year, monthValue] = value.split("-");
      setMonth(new Date(Number(year), Number(monthValue) - 1, 1));
      return;
    }

    const [year, monthValue, day] = value.split("-");
    setMonth(new Date(Number(year), Number(monthValue) - 1, Number(day)));
  };

  const renderReservationCard = reservation => (
    <Paper
      key={reservation.id}
      className={classes.reservation}
      elevation={0}
      style={{
        backgroundColor: reservation.service?.color || "#E8F3FF",
        borderColor: reservation.service?.color || "#B0C6DF",
      }}
      onClick={() => openReservationModal(reservation)}
    >
      <Typography variant="body2" noWrap>
        {formatTime12(reservation.startsAt, reservation.service?.timeZone)}{" "}
        {privacyCensorSettings.contactNames
          ? "Contacto privado"
          : reservation.customerName || reservation.contact?.name || reservation.title}
      </Typography>
      <Typography variant="caption" color="textSecondary" noWrap>
        {reservation.service?.name} - {formatReservationStatus(reservation.status)}
      </Typography>
    </Paper>
  );

  const getVisibleCalendarColumns = () => {
    if (calendarView === "day") return 1;
    if (calendarView === "week") return 1;
    return 7;
  };

  const getVisibleCalendarMinWidth = () => {
    if (calendarView === "day") return 0;
    if (calendarView === "week") return 0;
    return 980;
  };

  const updateReservationField = (index, value) => {
    setReservationForm(prev => {
      const customFields = [...(prev.customFields || [])];
      customFields[index] = { ...customFields[index], value };
      return { ...prev, customFields };
    });
  };

  const renderReservationCustomField = (field, index) => {
    if (field.type === "select") {
      return (
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>{field.name}</InputLabel>
          <Select
            label={field.name}
            value={field.value || NO_CUSTOM_FIELD_CONDITION}
            onChange={event =>
              updateReservationField(
                index,
                event.target.value === NO_CUSTOM_FIELD_CONDITION ? "" : event.target.value
              )
            }
          >
            <MenuItem value={NO_CUSTOM_FIELD_CONDITION}>Seleccionar valor</MenuItem>
            {(field.options || []).filter(Boolean).map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (field.type === "multiple") {
      const selectedValues = field.value
        ? String(field.value).split(",").map(value => value.trim()).filter(Boolean)
        : [];
      return (
        <FormControl variant="outlined" margin="dense" fullWidth>
          <InputLabel>{field.name}</InputLabel>
          <Select
            multiple
            label={field.name}
            value={selectedValues}
            onChange={event => updateReservationField(index, (event.target.value || []).join(", "))}
            renderValue={selected => selected.join(", ")}
          >
            {(field.options || []).filter(Boolean).map(option => (
              <MenuItem key={option} value={option}>
                <Checkbox checked={selectedValues.includes(option)} />
                <span>{option}</span>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        label={field.name}
        type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
        value={field.value || ""}
        onChange={event => updateReservationField(index, event.target.value)}
        variant="outlined"
        margin="dense"
        multiline={field.type === "textarea"}
        minRows={field.type === "textarea" ? 2 : undefined}
        fullWidth
        InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
      />
    );
  };

  const handleReservationServiceChange = serviceId => {
    if (serviceId === "__create__") {
      openServiceModal(null, "reservation");
      return;
    }

    const service = services.find(item => String(item.id) === String(serviceId));
    setReservationForm(prev => ({
      ...prev,
      serviceId,
      startsAt: "",
      participants: service?.hasPersons ? Number(service.minPersons || 1) : 1,
      customFields: buildReservationCustomFields(service, prev.customFields || []),
    }));
    setReservationSlotStartDate(prev => prev || toDateInput(new Date()));
  };

  const renderReservationSlots = () => {
    if (!reservationForm.serviceId) {
      return (
        <div className={classes.slotEmpty}>
          <Typography variant="body2" color="textSecondary">
            Selecciona un servicio para ver los horarios disponibles.
          </Typography>
        </div>
      );
    }

    const selectedStartsAt = reservationForm.startsAt
      ? reservationForm.startsAt.slice(0, 16)
      : "";
    const selectedSlot = reservationSlots
      .reduce((items, day) => items.concat(day.slots || []), [])
      .find(slot => slot.startsAt.slice(0, 16) === selectedStartsAt);
    const hasSlots = reservationSlots.some(day => day.slots.length);
    const moveSlotWindow = amount => {
      setReservationSlotStartDate(prev => {
        const baseDate = prev || toDateInput(new Date());
        return toDateInput(addDays(new Date(`${baseDate}T00:00:00`), amount));
      });
    };

    return (
      <div className={classes.slotsPanel}>
        <div className={classes.slotsHeader}>
          <div>
            <Typography variant="subtitle2">Horarios disponibles</Typography>
            {selectedStartsAt && (
              <>
                <Typography variant="caption" color="textSecondary">
                  Seleccionado: {formatDateTimeInTimeZone(selectedStartsAt, undefined)}
                </Typography>
                {!!selectedSlot?.resources?.length && (
                  <div className={classes.slotResourceBadges}>
                    {selectedSlot.resources.map(resource => (
                      <Chip
                        key={resource.resourceId}
                        size="small"
                        variant="outlined"
                        color={Number(resource.available || 0) > 0 ? "primary" : "secondary"}
                        label={`${resource.name}: ${resource.available} disponibles`}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div className={classes.slotsNav}>
            <Tooltip title="Dias anteriores">
              <span>
                <IconButton size="small" onClick={() => moveSlotWindow(-7)}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Dias siguientes">
              <span>
                <IconButton size="small" onClick={() => moveSlotWindow(7)}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            {loadingReservationSlots && <CircularProgress size={20} />}
          </div>
        </div>

        {!loadingReservationSlots && !hasSlots && (
          <div className={classes.slotEmpty}>
            <Typography variant="body2" color="textSecondary">
              No hay horarios disponibles en los proximos 7 dias.
            </Typography>
          </div>
        )}

        {reservationSlots.map(day => {
          if (!day.slots.length) return null;

          return (
            <div key={day.date} className={classes.slotDay}>
              <Typography variant="caption" color="textSecondary">
                {formatSlotDayLabel(day.date)}
              </Typography>
              <div className={classes.slotGrid}>
                {day.slots.map(slot => {
                  const slotValue = slot.startsAt.slice(0, 16);
                  const selected = selectedStartsAt === slotValue;

                  return (
                    <Button
                      key={slot.startsAt}
                      size="small"
                      variant={selected ? "contained" : "outlined"}
                      color="primary"
                      className={
                        selected ? classes.selectedSlotButton : classes.slotButton
                      }
                      onClick={() =>
                        setReservationForm(prev => ({
                          ...prev,
                          startsAt: slot.startsAt,
                        }))
                      }
                    >
                      {slot.time}
                    </Button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderAgendaCountdowns = () => {
    if (!reservationForm.id) return null;

    const countdowns = reservationForm.reminders || [];
    const service = services.find(
      item => String(item.id) === String(reservationForm.serviceId)
    );
    const timeZone = service?.timeZone || reservationForm.service?.timeZone;

    return (
      <Grid item xs={12}>
        <Typography variant="subtitle2">Countdowns programados</Typography>
        <div className={classes.countdownSummary}>
          {countdowns.length ? (
            countdowns.map((reminder, index) => {
              const sequenceName =
                reminder.chatbotSequence?.name ||
                (reminder.chatbotSequenceId
                  ? `Secuencia #${reminder.chatbotSequenceId}`
                  : "Secuencia");

              return (
                <Paper
                  key={reminder.id || index}
                  className={classes.countdownCard}
                  elevation={0}
                >
                  <div className={classes.countdownCardHeader}>
                    <div>
                      <Typography variant="subtitle2">
                        Countdown {index + 1}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatDateTimeInTimeZone(reminder.scheduledAt, timeZone)} ·{" "}
                        {sequenceName} · {formatAgendaReminderStatus(reminder.status)}
                      </Typography>
                      {reminder.lastError && (
                        <Typography variant="caption" color="error">
                          {reminder.lastError}
                        </Typography>
                      )}
                    </div>
                    <div className={classes.countdownCardActions}>
                      <IconButton
                        size="small"
                        onClick={() => setDeletingCountdown(reminder)}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  </div>
                </Paper>
              );
            })
          ) : (
            <Typography variant="body2" color="textSecondary">
              Esta reserva no tiene countdowns programados.
            </Typography>
          )}
        </div>
      </Grid>
    );
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title="Eliminar servicio"
        open={!!deletingService}
        onClose={() => setDeletingService(null)}
        onConfirm={handleDeleteService}
      >
        Las reservas asociadas a este servicio tambien se eliminaran.
      </ConfirmationModal>
      <ConfirmationModal
        title="Eliminar reserva"
        open={!!deletingReservation}
        onClose={() => setDeletingReservation(null)}
        onConfirm={handleDeleteReservation}
      >
        Esta accion no se puede deshacer.
      </ConfirmationModal>
      <ConfirmationModal
        title="Eliminar recurso"
        open={!!deletingResource}
        onClose={() => setDeletingResource(null)}
        onConfirm={handleDeleteResource}
      >
        Los servicios dejaran de validar este recurso compartido.
      </ConfirmationModal>
      <ConfirmationModal
        title="Eliminar countdown"
        open={!!deletingCountdown}
        onClose={() => setDeletingCountdown(null)}
        onConfirm={handleDeleteCountdown}
      >
        Este countdown dejara de ejecutarse para esta reserva.
      </ConfirmationModal>

      <MainHeader className={classes.agendaHeader}>
        <Title>{i18n.t("agendaPage.title")}</Title>
        <MainHeaderButtonsWrapper className={classes.agendaHeaderButtons}>
          <Button
            color="primary"
            variant="outlined"
            startIcon={<Add />}
            onClick={() => openServiceModal()}
          >
            Crear servicio
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<EventAvailable />}
            onClick={() => openReservationModal(null, new Date())}
            disabled={!services.length}
          >
            Reserva
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab value="calendar" label="Calendario" />
          <Tab value="services" label="Servicios" />
          <Tab value="resources" label="Recursos" />
        </Tabs>

        {tab === "calendar" && (
          <>
            <div className={classes.toolbar}>
              <FormControl variant="outlined" margin="dense">
                <InputLabel>Vista</InputLabel>
                <Select
                  value={calendarView}
                  label="Vista"
                  onChange={event => setCalendarView(event.target.value)}
                >
                  <MenuItem value="day">Dia</MenuItem>
                  <MenuItem value="week">Semana</MenuItem>
                  <MenuItem value="month">Mes</MenuItem>
                </Select>
              </FormControl>
              <FormControl variant="outlined" margin="dense">
                <InputLabel>Servicio / calendario</InputLabel>
                <Select
                  value={selectedServiceId || ALL_SERVICES_FILTER}
                  label="Servicio / calendario"
                  onChange={event =>
                    setSelectedServiceId(
                      event.target.value === ALL_SERVICES_FILTER ? "" : event.target.value
                    )
                  }
                >
                  <MenuItem value={ALL_SERVICES_FILTER}>Todos los servicios</MenuItem>
                  {services.map(service => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl variant="outlined" margin="dense">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={selectedStatus || ALL_STATUSES_FILTER}
                  label="Estado"
                  onChange={event =>
                    setSelectedStatus(
                      event.target.value === ALL_STATUSES_FILTER ? "" : event.target.value
                    )
                  }
                >
                  <MenuItem value={ALL_STATUSES_FILTER}>Todos los estados</MenuItem>
                  {reservationStatusOptions.map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={calendarView === "month" ? "Mes" : "Fecha"}
                type={calendarView === "month" ? "month" : "date"}
                margin="dense"
                variant="outlined"
                value={
                  calendarView === "month"
                    ? `${month.getFullYear()}-${pad(month.getMonth() + 1)}`
                    : toDateInput(month)
                }
                onChange={event => handleCalendarDateChange(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              {loading && <CircularProgress size={20} />}
            </div>

            <div
              className={classes.calendar}
              style={{
                gridTemplateColumns: `repeat(${getVisibleCalendarColumns()}, minmax(180px, 1fr))`,
                minWidth: getVisibleCalendarMinWidth(),
              }}
            >
              {calendarDays.map(day => (
                <Paper key={day.toISOString()} className={classes.day} elevation={0}>
                  <div className={classes.dayHeader}>
                    <Typography
                      variant="subtitle2"
                      color={day.getMonth() === month.getMonth() ? "textPrimary" : "textSecondary"}
                    >
                      {calendarView === "week"
                        ? `${weekdays[day.getDay()]} ${day.getDate()}`
                        : `${weekdays[day.getDay()].slice(0, 3)} ${day.getDate()}`}
                    </Typography>
                    <IconButton size="small" onClick={() => openReservationModal(null, day)}>
                      <Add fontSize="small" />
                    </IconButton>
                  </div>
                  {reservationsByDate(day).map(renderReservationCard)}
                </Paper>
              ))}
            </div>
          </>
        )}

        {tab === "services" && (
          <div className={classes.serviceList}>
            {services.map(service => (
              <Paper key={service.id} className={classes.serviceCard} elevation={0}>
                <div className={classes.serviceHeader}>
                  <span
                    className={classes.colorDot}
                    style={{ backgroundColor: service.color }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" noWrap>{service.name}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {service.durationMinutes} min · cada {service.intervalMinutes} min · {service.timeZone}
                    </Typography>
                  </div>
                  <IconButton size="small" onClick={() => openServiceModal(service)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeletingService(service)}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </div>
                <Typography variant="body2" color="textSecondary">
                  {service.description || "Sin descripcion"}
                </Typography>
                {service.widgetToken && (
                  <>
                    <Typography variant="caption" color="textSecondary">
                      Widget HTML
                    </Typography>
                    <div className={classes.widgetBox}>
                      {widgetScript(service)}
                      <Tooltip title="Copiar">
                        <IconButton
                          size="small"
                          onClick={() => {
                            navigator.clipboard.writeText(widgetScript(service));
                            toast.success("Script copiado");
                          }}
                        >
                          <FileCopyOutlined fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </>
                )}
              </Paper>
            ))}
          </div>
        )}

        {tab === "resources" && (
          <div className={classes.resourcesPanel}>
            <div className={classes.resourcesHeader}>
              <div>
                <Typography variant="subtitle1">Recursos</Typography>
                <Typography variant="body2" color="textSecondary">
                  Inventario compartido para validar disponibilidad entre servicios.
                </Typography>
              </div>
              <Button
                color="primary"
                variant="contained"
                startIcon={<Add />}
                onClick={handleCreateResource}
              >
                Crear recurso
              </Button>
            </div>

            {!resources.length ? (
              <div className={classes.slotEmpty}>
                <Typography variant="body2" color="textSecondary">
                  No hay recursos configurados.
                </Typography>
              </div>
            ) : (
              <div className={classes.resourcesList}>
                {resources.map(resource => (
                  <Paper key={resource.id} className={classes.resourceCard} elevation={0}>
                    <div className={classes.resourceRow}>
                    <TextField
                      label="Nombre"
                      value={resource.name || ""}
                      onChange={event =>
                        setResources(prev =>
                          prev.map(item =>
                            item.id === resource.id
                              ? { ...item, name: event.target.value }
                              : item
                          )
                        )
                      }
                      onBlur={() => handleUpdateResource(resource, { name: resource.name })}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                    <TextField
                      label="Cantidad disponible"
                      type="number"
                      value={resource.quantity || 1}
                      onChange={event =>
                        setResources(prev =>
                          prev.map(item =>
                            item.id === resource.id
                              ? { ...item, quantity: event.target.value }
                              : item
                          )
                        )
                      }
                      onBlur={() =>
                        handleUpdateResource(resource, { quantity: resource.quantity })
                      }
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                    <div className={classes.resourceStatus}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={resource.isActive !== false}
                          onChange={event =>
                            handleUpdateResource(resource, {
                              isActive: event.target.checked,
                            })
                          }
                          color="primary"
                        />
                      }
                      label="Activo"
                    />
                    </div>
                    <div className={classes.resourceActions}>
                    <IconButton size="small" onClick={() => setDeletingResource(resource)}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                    </div>
                    </div>
                  </Paper>
                ))}
              </div>
            )}
          </div>
        )}
      </Paper>

      <Dialog
        open={serviceModalOpen}
        onClose={closeServiceModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{serviceForm.id ? "Editar servicio" : "Nuevo servicio"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nombre"
                value={serviceForm.name}
                onChange={event => setServiceForm({ ...serviceForm, name: event.target.value })}
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Descripcion"
                value={serviceForm.description}
                onChange={event =>
                  setServiceForm({ ...serviceForm, description: event.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Duracion"
                type="number"
                value={serviceForm.durationMinutes}
                onChange={event =>
                  setServiceForm({ ...serviceForm, durationMinutes: event.target.value })
                }
                helperText="Minutos"
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Intervalo"
                type="number"
                value={serviceForm.intervalMinutes}
                onChange={event =>
                  setServiceForm({ ...serviceForm, intervalMinutes: event.target.value })
                }
                helperText="Minutos"
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Espacio antes"
                type="number"
                value={serviceForm.bufferBeforeMinutes}
                onChange={event =>
                  setServiceForm({ ...serviceForm, bufferBeforeMinutes: event.target.value })
                }
                helperText="Minutos"
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Espacio despues"
                type="number"
                value={serviceForm.bufferAfterMinutes}
                onChange={event =>
                  setServiceForm({ ...serviceForm, bufferAfterMinutes: event.target.value })
                }
                helperText="Minutos"
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Reservas por slot"
                type="number"
                value={serviceForm.maxBookingsPerSlot}
                onChange={event =>
                  setServiceForm({ ...serviceForm, maxBookingsPerSlot: event.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={serviceForm.hasPersons === true}
                    onChange={event =>
                      setServiceForm({ ...serviceForm, hasPersons: event.target.checked })
                    }
                    color="primary"
                  />
                }
                label="Pedir personas"
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Min personas"
                type="number"
                value={serviceForm.minPersons}
                disabled={serviceForm.hasPersons !== true}
                onChange={event =>
                  setServiceForm({ ...serviceForm, minPersons: event.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Max personas"
                type="number"
                value={serviceForm.maxPersons}
                disabled={serviceForm.hasPersons !== true}
                onChange={event =>
                  setServiceForm({ ...serviceForm, maxPersons: event.target.value })
                }
                variant="outlined"
                margin="dense"
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={serviceForm.countPersonsAsCapacity === true}
                    disabled={serviceForm.hasPersons !== true}
                    onChange={event =>
                      setServiceForm({
                        ...serviceForm,
                        countPersonsAsCapacity: event.target.checked,
                      })
                    }
                    color="primary"
                  />
                }
                label="Contar personas contra reservas por slot"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Zona horaria</InputLabel>
                <Select
                  value={serviceForm.timeZone || ""}
                  label="Zona horaria"
                  onChange={event =>
                    setServiceForm({ ...serviceForm, timeZone: event.target.value })
                  }
                >
                  {timeZoneOptions.map(timeZone => (
                    <MenuItem key={timeZone} value={timeZone}>
                      {timeZone}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Conexion</InputLabel>
                <Select
                  value={serviceForm.connectionId || NO_SERVICE_CONNECTION}
                  label="Conexion"
                  onChange={event =>
                    setServiceForm({ ...serviceForm, connectionId: event.target.value })
                  }
                >
                  <MenuItem value={NO_SERVICE_CONNECTION}>Sin conexion</MenuItem>
                  {connections.map(connection => (
                    <MenuItem key={connection.id} value={connection.id}>
                      {connection.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Asignado</InputLabel>
                <Select
                  value={serviceForm.userId || NO_SERVICE_USER}
                  label="Asignado"
                  onChange={event =>
                    setServiceForm({ ...serviceForm, userId: event.target.value })
                  }
                >
                  <MenuItem value={NO_SERVICE_USER}>Sin asignar</MenuItem>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Color"
                variant="outlined"
                margin="dense"
                fullWidth
                value={serviceForm.color}
                onChange={event =>
                  setServiceForm({ ...serviceForm, color: event.target.value })
                }
                onBlur={event => {
                  if (!isValidHexColor(event.target.value)) return;
                  const color = normalizeHexColor(event.target.value);
                  setServiceForm(prev => ({ ...prev, color }));
                  saveCustomColor(color);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <div
                        className={classes.colorAdornment}
                        style={{ backgroundColor: serviceForm.color }}
                      />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={() => setServiceColorPickerOpen(true)}
                    >
                      <Colorize />
                    </IconButton>
                  ),
                }}
              />
              <ColorPicker
                open={serviceColorPickerOpen}
                currentColor={serviceForm.color}
                handleClose={() => setServiceColorPickerOpen(false)}
                onChange={color => setServiceForm(prev => ({ ...prev, color }))}
              />
            </Grid>
          </Grid>

          <div className={classes.serviceHeader} style={{ marginTop: 12 }}>
            <Typography variant="subtitle2">Recursos requeridos</Typography>
            <Button
              size="small"
              color="primary"
              startIcon={<Add />}
              onClick={addServiceResource}
              disabled={!resources.length}
            >
              Agregar recurso
            </Button>
          </div>
          {!resources.length && (
            <Typography variant="caption" color="textSecondary">
              Crea recursos en la pestaña Recursos para asociarlos a servicios.
            </Typography>
          )}
          {(serviceForm.serviceResources || []).map((rule, index) => (
            <Grid container spacing={1} alignItems="center" key={index}>
              <Grid item xs={12} md={4}>
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel>Recurso</InputLabel>
                  <Select
                    value={rule.resourceId || ""}
                    label="Recurso"
                    onChange={event =>
                      updateServiceResource(index, { resourceId: event.target.value })
                    }
                  >
                    {resources.map(resource => (
                      <MenuItem key={resource.id} value={resource.id}>
                        {resource.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel>Consumo</InputLabel>
                  <Select
                    value={rule.requirementType || "fixed"}
                    label="Consumo"
                    onChange={event =>
                      updateServiceResource(index, {
                        requirementType: event.target.value,
                      })
                    }
                  >
                    <MenuItem value="fixed">Fijo</MenuItem>
                    <MenuItem value="perPerson">Por persona</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={8} md={3}>
                <TextField
                  label="Cantidad"
                  type="number"
                  value={rule.quantity || 1}
                  onChange={event =>
                    updateServiceResource(index, { quantity: event.target.value })
                  }
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
              </Grid>
              <Grid item xs={4} md={2}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const serviceResources = serviceForm.serviceResources.filter(
                      (_, ruleIndex) => ruleIndex !== index
                    );
                    setServiceForm({
                      ...serviceForm,
                      serviceResources,
                      hasResources: serviceResources.length > 0,
                    });
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}

          <Typography variant="subtitle2" style={{ marginTop: 12 }}>
            Horarios disponibles
          </Typography>
          {(serviceForm.availability || []).map((rule, index) => (
            <div className={classes.availabilityRow} key={rule.day}>
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={rule.enabled !== false}
                    onChange={event => {
                      const availability = [...serviceForm.availability];
                      availability[index] = {
                        ...rule,
                        enabled: event.target.checked,
                      };
                      setServiceForm({ ...serviceForm, availability });
                    }}
                  />
                }
                label={weekdays[rule.day]}
              />
              <TextField
                type="time"
                value={rule.startTime}
                variant="outlined"
                margin="dense"
                onChange={event => {
                  const availability = [...serviceForm.availability];
                  availability[index] = { ...rule, startTime: event.target.value };
                  setServiceForm({ ...serviceForm, availability });
                }}
              />
              <TextField
                type="time"
                value={rule.endTime}
                variant="outlined"
                margin="dense"
                onChange={event => {
                  const availability = [...serviceForm.availability];
                  availability[index] = { ...rule, endTime: event.target.value };
                  setServiceForm({ ...serviceForm, availability });
                }}
              />
            </div>
          ))}

          <Typography variant="subtitle2" style={{ marginTop: 12 }}>
            Campos personalizados
          </Typography>
          {normalizeServiceCustomFields(serviceForm.customFields).map((field, index) => (
            <div className={classes.customFieldBox} key={index}>
              <div className={classes.customFieldRow}>
                <TextField
                  label="Dato"
                  value={field.name}
                  onChange={event => updateServiceCustomField(index, { name: event.target.value })}
                  variant="outlined"
                  margin="dense"
                />
                <FormControl variant="outlined" margin="dense">
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    value={field.type || "text"}
                    label="Tipo"
                    onChange={event =>
                      updateServiceCustomField(index, {
                        type: event.target.value,
                        options:
                          event.target.value === "select" || event.target.value === "multiple"
                            ? field.options || []
                            : [],
                      })
                    }
                  >
                    {customFieldTypes.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={field.required === true}
                      onChange={event => updateServiceCustomField(index, { required: event.target.checked })}
                    />
                  }
                  label="Requerido"
                />
                <IconButton
                  size="small"
                  onClick={() => {
                    const customFields = serviceForm.customFields.filter((_, i) => i !== index);
                    setServiceForm({ ...serviceForm, customFields });
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </div>
              {renderCustomFieldConditionalControls(field, index)}
              {(field.type === "select" || field.type === "multiple") && (
                <div className={classes.customFieldOptions}>
                  {(field.options || []).map((option, optionIndex) => (
                    <div className={classes.customFieldOptionRow} key={`${index}-${optionIndex}`}>
                      <TextField
                        label="Opcion"
                        value={option}
                        onChange={event => updateServiceCustomFieldOption(index, optionIndex, event.target.value)}
                        variant="outlined"
                        margin="dense"
                        fullWidth
                      />
                      <IconButton
                        size="small"
                        onClick={() =>
                          updateServiceCustomField(index, {
                            options: (field.options || []).filter((_, itemIndex) => itemIndex !== optionIndex),
                          })
                        }
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                  <Button
                    color="primary"
                    onClick={() =>
                      updateServiceCustomField(index, {
                        options: [...(field.options || []), ""],
                      })
                    }
                  >
                    Agregar opcion
                  </Button>
                </div>
              )}
            </div>
          ))}
          <Button color="primary" startIcon={<Add />} onClick={addServiceField}>
            Agregar campo
          </Button>

          <Typography variant="subtitle2" style={{ marginTop: 12 }}>
            Recordar minutos antes
          </Typography>
          {(serviceForm.reminders || []).map((reminder, index) => (
            <Grid
              container
              spacing={1}
              key={index}
              className={classes.countdownRow}
            >
              <Grid item xs={4} md={2}>
                <TextField
                  label="Antes"
                  type="number"
                  value={reminder.value}
                  onChange={event => {
                    const reminders = [...serviceForm.reminders];
                    reminders[index] = { ...reminder, value: event.target.value };
                    setServiceForm({ ...serviceForm, reminders });
                  }}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
              </Grid>
              <Grid item xs={8} md={3}>
                <FormControl variant="outlined" margin="dense" fullWidth>
                  <InputLabel>Unidad</InputLabel>
                  <Select
                    value={reminder.unit || "hours"}
                    label="Unidad"
                    onChange={event => {
                      const reminders = [...serviceForm.reminders];
                      reminders[index] = { ...reminder, unit: event.target.value };
                      setServiceForm({ ...serviceForm, reminders });
                    }}
                  >
                    <MenuItem value="minutes">Minutos</MenuItem>
                    <MenuItem value="hours">Horas</MenuItem>
                    <MenuItem value="days">Dias</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={10} md={6}>
                {renderSequenceSelector(reminder, index)}
              </Grid>
              <Grid item xs={2} md={1}>
                <IconButton
                  size="small"
                  onClick={() => {
                    const reminders = serviceForm.reminders.filter((_, i) => i !== index);
                    setServiceForm({ ...serviceForm, reminders });
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>
          ))}
          <Button color="primary" startIcon={<Add />} onClick={addServiceReminder}>
            Agregar recordatorio
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeServiceModal} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button onClick={handleSaveService} color="primary" variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={reservationModalOpen}
        onClose={() => setReservationModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{reservationForm.id ? "Editar reserva" : "Nueva reserva"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Servicio</InputLabel>
                <Select
                  value={reservationForm.serviceId}
                  label="Servicio"
                  onChange={event => handleReservationServiceChange(event.target.value)}
                >
                  <MenuItem value="__create__">Crear servicio...</MenuItem>
                  {services.map(service => (
                    <MenuItem key={service.id} value={service.id}>
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div className={classes.serviceActions}>
                <Button
                  color="primary"
                  disabled={!reservationForm.serviceId}
                  onClick={() => openEditReservationService(reservationForm.serviceId)}
                >
                  Editar servicio
                </Button>
              </div>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={contactOptions}
                value={selectedReservationContact}
                getOptionSelected={(option, value) =>
                  String(option.id) === String(value.id)
                }
                getOptionLabel={option =>
                  option
                    ? privacyCensorSettings.contactNames
                      ? "Contacto privado"
                      : `${option.name} - ${option.number}`
                    : ""
                }
                onInputChange={(_, value) => setContactSearch(value)}
                onChange={(_, value) => {
                  setSelectedReservationContact(value);
                  if (!value) {
                    setReservationForm(prev => ({
                      ...prev,
                      contactId: "",
                      customerName: "",
                      customerPhone: "",
                      customerEmail: "",
                    }));
                    return;
                  }
                  setReservationForm(prev => ({
                    ...prev,
                    contactId: value.id,
                    customerName: value.name || "",
                    customerPhone: value.number || "",
                    customerEmail: value.email || "",
                  }));
                }}
                renderOption={renderContactOption}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Contacto"
                    variant="outlined"
                    margin="dense"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nombre"
                value={
                  privacyCensorSettings.contactNames
                    ? "Contacto privado"
                    : reservationForm.customerName
                }
                variant="outlined"
                margin="dense"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Telefono"
                value={
                  privacyCensorSettings.contactNames
                    ? "Telefono privado"
                    : reservationForm.customerPhone
                }
                variant="outlined"
                margin="dense"
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Correo"
                value={
                  privacyCensorSettings.contactNames
                    ? "Correo privado"
                    : reservationForm.customerEmail
                }
                variant="outlined"
                margin="dense"
                fullWidth
                disabled
              />
            </Grid>
            {reservationService?.hasPersons && (
              <Grid item xs={12} md={6}>
                <TextField
                  label="Personas"
                  type="number"
                  value={reservationForm.participants || reservationService.minPersons || 1}
                  onChange={event =>
                    setReservationForm({
                      ...reservationForm,
                      startsAt: "",
                      participants: event.target.value,
                    })
                  }
                  inputProps={{
                    min: reservationService.minPersons || 1,
                    max: reservationService.maxPersons || undefined,
                  }}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />
              </Grid>
            )}
            <Grid item xs={12}>
              {renderReservationSlots()}
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="outlined" margin="dense" fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={reservationForm.status || "pending"}
                  label="Estado"
                  onChange={event =>
                    setReservationForm({ ...reservationForm, status: event.target.value })
                  }
                >
                  <MenuItem value="pending">Pendiente</MenuItem>
                  <MenuItem value="confirmed">Confirmada</MenuItem>
                  <MenuItem value="rescheduled">Reprogramada</MenuItem>
                  <MenuItem value="cancelled">Cancelada</MenuItem>
                  <MenuItem value="completed">Completada</MenuItem>
                  <MenuItem value="no_show">No asistio</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {getVisibleCustomFields(
              (reservationForm.customFields || []).map((field, index) => ({ ...field, index }))
            ).map(field => (
              <Grid item xs={12} md={6} key={`${field.name}-${field.index}`}>
                {renderReservationCustomField(field, field.index)}
              </Grid>
            ))}
            <Grid item xs={12}>
              <TextField
                label="Notas"
                value={reservationForm.notes || ""}
                onChange={event =>
                  setReservationForm({ ...reservationForm, notes: event.target.value })
                }
                variant="outlined"
                margin="dense"
                multiline
                minRows={3}
                fullWidth
              />
            </Grid>
            {reservationForm.id && (
              <Grid item xs={12}>
                <Chip
                  icon={<Public />}
                  size="small"
                  label={`Origen: ${reservationForm.source || "manual"}`}
                />
              </Grid>
            )}
            {renderAgendaCountdowns()}
          </Grid>
        </DialogContent>
        <DialogActions>
          {reservationForm.id && (
            <Button
              onClick={() => setDeletingReservation(reservationForm)}
              color="secondary"
              variant="outlined"
              startIcon={<DeleteOutline />}
            >
              Eliminar
            </Button>
          )}
          <Button
            onClick={() => setReservationModalOpen(false)}
            color="secondary"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveReservation}
            color="primary"
            variant="contained"
            disabled={!reservationForm.startsAt}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <ChatbotModal
        open={sequenceModalOpen}
        onClose={handleSequenceModalClose}
        onSave={handleSequenceSaved}
        sequenceId={sequenceModalId}
        defaultConnectionId={toOptionalString(serviceForm.connectionId, NO_SERVICE_CONNECTION)}
        initialSequenceName="Recordatorio de reserva"
      />
    </MainContainer>
  );
};

export default Agenda;

